import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { useShallow } from "zustand/shallow"; // apply shallow comparison (to avoid unnecessary re-renders)

//* Defined slices close to state-only behavior. Do not put more logic into zustand slices than necessary.
import { createAuthSlice, AuthSlice } from "./globalStoreSlices/authSlice";
import { createSsotSlice, SsotSlice } from "./globalStoreSlices/ssotSlice";
import {
  createUserChoicesSlice,
  UserChoiceState,
} from "./globalStoreSlices/userChoices";
import {
  createUserChoicesProfileSelectorSlice,
  UserChoiceProfileSelectorState,
} from "./globalStoreSlices/userChoicesProfileSelector";
import {
  createOriginSlice,
  OriginSlice,
} from "./globalStoreSlices/originSlice";
import {
  createSituationSlice,
  SituationSlice,
} from "./globalStoreSlices/situationSlice";
import {
  createThoughtSlice,
  ThoughtSlice,
} from "./globalStoreSlices/thoughtSlice";
import {
  createUnconfirmedThoughtSlice,
  UnconfirmedThoughtSlice,
} from "./globalStoreSlices/unconfirmedThoughtSlice";
import {
  createTargetSlice,
  TargetSlice,
} from "./globalStoreSlices/targetSlice";
import {
  createUserProfileSlice,
  UserProfileSlice,
} from "./globalStoreSlices/profileSlice";
import { updateSession } from "@/core/supabase/middlewareTryingToSolve502";

// Combine all slices into one type
export type GlobalState = AuthSlice &
  SsotSlice &
  UserChoiceState &
  UserChoiceProfileSelectorState &
  OriginSlice &
  SituationSlice &
  ThoughtSlice &
  UnconfirmedThoughtSlice &
  TargetSlice &
  UserProfileSlice;

// Create the store with all slices
export const useGlobalStore = create<GlobalState>()(
  devtools(
    (...a) => ({
      ...createAuthSlice(...a),
      ...createSsotSlice(...a),
      ...createUserChoicesSlice(...a), //TODO consider separate/pernament store
      ...createUserChoicesProfileSelectorSlice(...a), //TODO consider separate/pernament store
      ...createOriginSlice(...a),
      ...createSituationSlice(...a),
      ...createThoughtSlice(...a),
      ...createUnconfirmedThoughtSlice(...a),
      ...createTargetSlice(...a),
      ...createUserProfileSlice(...a),
    }),
    { name: "global-store" }
  )
);

// Custom hooks to access specific slices of the store
export const useAuth = () =>
  useGlobalStore(
    useShallow((state) => ({
      user: state.user,
      accessToken: state.accessToken,
      refreshToken: state.refreshToken,
      isInitialized: state.isInitialized,
      setUser: state.setUser,
      setTokens: state.setTokens,
      logout: state.logout,
    }))
  );

export const useSsot = () =>
  useGlobalStore(
    useShallow((state) => ({
      //////* Languages
      languages: state.languages,
      setLanguages: state.setLanguages,
      setLanguagesLoading: state.setLanguagesLoading,
      setLanguagesError: state.setLanguagesError,
      resetLanguages: state.resetLanguages,
      // Initial fetch state
      initFetchedLanguages: state.initFetchedLanguages,
      setInitFetchedLanguages: state.setInitFetchedLanguages,

      //////* Mindstring Prototypes
      mindstringPrototypes: state.mindstringPrototypes,
      setMindstringPrototypes: state.setMindstringPrototypes,
      setMindstringPrototypesLoading: state.setMindstringPrototypesLoading,
      setMindstringPrototypesError: state.setMindstringPrototypesError,
      resetMindstringPrototypes: state.resetMindstringPrototypes,
      // Initial fetch state
      initFetchedMindstringPrototypes: state.initFetchedMindstringPrototypes,
      setInitFetchedMindstringPrototypes:
        state.setInitFetchedMindstringPrototypes,

      //////* Situation categories
      situationCategories: state.situationCategories,
      setSituationCategories: state.setSituationCategories,
      setSituationCategoriesLoading: state.setSituationCategoriesLoading,
      setSituationCategoriesError: state.setSituationCategoriesError,
      resetSituationCategories: state.resetSituationCategories,
      // Initial fetch state
      initFetchedSituationCategories: state.initFetchedSituationCategories,
      setInitFetchedSituationCategories:
        state.setInitFetchedSituationCategories,

      //////* Prototype-to-Category mapping
      prototypeToCategoryMap: state.prototypeToCategoryMap,
      setPrototypeToCategoryMap: state.setPrototypeToCategoryMap,
      setPrototypeToCategoryMapLoading: state.setPrototypeToCategoryMapLoading,
      setPrototypeToCategoryMapError: state.setPrototypeToCategoryMapError,
      resetPrototypeToCategoryMap: state.resetPrototypeToCategoryMap,
      // Initial fetch state
      initFetchedPrototypeToCategoryMap:
        state.initFetchedPrototypeToCategoryMap,
      setInitFetchedPrototypeToCategoryMap:
        state.setInitFetchedPrototypeToCategoryMap,
    }))
  );

export const useUserChoices = () =>
  useGlobalStore(
    useShallow((state) => ({
      //////* Languages
      selectedLanguage: state.selectedLanguage,
      setSelectedLanguage: state.setSelectedLanguage,
      resetSelectedLanguage: state.resetSelectedLanguage,

      //////* Situation categories
      selectedSituationCategories: state.selectedSituationCategories,
      setSelectedSituationCategories: state.setSelectedSituationCategories,
      resetSelectedSituationCategories: state.resetSelectedSituationCategories,

      //////* Mindstring Prototype
      selectedMindstringPrototype: state.selectedMindstringPrototype,
      setSelectedMindstringPrototype: state.setSelectedMindstringPrototype,
      resetSelectedMindstringPrototype: state.resetSelectedMindstringPrototype,

      //////* Situation
      selectedSituation: state.selectedSituation,
      setSelectedSituation: state.setSelectedSituation,
      resetSelectedSituation: state.resetSelectedSituation,
      // new situation
      newSituation: state.newSituation,
      setNewSituation: state.setNewSituation,
      resetNewSituation: state.resetNewSituation,
      // manage dialog
      situationDialogMode: state.situationDialogMode,
      setSituationDialogMode: state.setSituationDialogMode,
      resetSituationDialogMode: state.resetSituationDialogMode,
      // situation to be edited
      situationToEdit: state.situationToEdit,
      setSituationToEdit: state.setSituationToEdit,
      resetSituationToEdit: state.resetSituationToEdit,

      //////* Target
      selectedTarget: state.selectedTarget,
      setSelectedTarget: state.setSelectedTarget,
      resetSelectedTarget: state.resetSelectedTarget,
      // new target
      newTarget: state.newTarget,
      setNewTarget: state.setNewTarget,
      resetNewTarget: state.resetNewTarget,
      // manage dialog
      targetDialogMode: state.targetDialogMode,
      setTargetDialogMode: state.setTargetDialogMode,
      resetTargetDialogMode: state.resetTargetDialogMode,
      // target to be edited
      targetToEdit: state.targetToEdit,
      setTargetToEdit: state.setTargetToEdit,
      resetTargetToEdit: state.resetTargetToEdit,
      // preview target
      isPreviewOpen: state.isPreviewOpen,
      setPreviewOpen: state.setPreviewOpen,

      //////* Origin
      selectedFile: state.selectedFile,
      setSelectedFile: state.setSelectedFile,
      resetSelectedFile: state.resetSelectedFile,
      // file upload clear trigger
      fileKeyTrigger: state.fileKeyTrigger,
      setFileKeyTrigger: state.setFileKeyTrigger,
      // new origin
      newOrigin: state.newOrigin,
      setNewOrigin: state.setNewOrigin,
      resetNewOrigin: state.resetNewOrigin,

      //////* Insert thoughts
      isInsertThoughtsDialogOpen: state.isInsertThoughtsDialogOpen,
      whereInsertThoughtsDialog: state.whereInsertThoughtsDialog,
      openInsertThoughtsDialog: state.openInsertThoughtsDialog,
      closeInsertThoughtsDialog: state.closeInsertThoughtsDialog,

      //////* Thought Extraction
      isOpenEditorDialog: state.isOpenEditorDialog,
      setIsOpenEditorDialog: state.setIsOpenEditorDialog,
      editorContent: state.editorContent,
      setEditorContent: state.setEditorContent,

      //////* Questionnaire
      isOpenQuestionnaireDialog: state.isOpenQuestionnaireDialog,
      setIsOpenQuestionnaireDialog: state.setIsOpenQuestionnaireDialog,
    }))
  );

export const useUserChoicesProfileSelector = () =>
  useGlobalStore(
    useShallow((state) => ({
      // select situations
      checkedSituations: state.checkedSituations,
      addCheckedSituation: state.addCheckedSituation,
      removeCheckedSituation: state.removeCheckedSituation,
      resetCheckedSituations: state.resetCheckedSituations,
      // select thoughts
      checkedThoughts: state.checkedThoughts,
      addCheckedThought: state.addCheckedThought,
      removeCheckedThought: state.removeCheckedThought,
      resetCheckedThoughts: state.resetCheckedThoughts,
      // edit mindstrings
      editedMindstrings: state.editedMindstrings,
      setEditedMindstrings: state.setEditedMindstrings,
      resetEditedMindstrings: state.resetEditedMindstrings,
      // active situation category (where the user is currently)
      activeSituationCategory: state.activeSituationCategory,
      setActiveSituationCategory: state.setActiveSituationCategory,
      // thought sort order
      sortOrder: state.sortOrder,
      setSortOrder: state.setSortOrder,
      // search for language
      searchForLanguage: state.searchForLanguage,
      setSearchForLanguage: state.setSearchForLanguage,
      resetSearchForLanguage: state.resetSearchForLanguage,
    }))
  );

export const useOrigins = () =>
  useGlobalStore(
    useShallow((state) => ({
      // active source type (tracking which content source was selected)
      activeSourceType: state.activeSourceType,
      setActiveSourceType: state.setActiveSourceType,
      // grabbed website
      grabbedWebsite: state.grabbedWebsite,
      setGrabbedWebsite: state.setGrabbedWebsite,
      setGrabbedWebsiteLoading: state.setGrabbedWebsiteLoading,
      setGrabbedWebsiteError: state.setGrabbedWebsiteError,
      resetGrabbedWebsite: state.resetGrabbedWebsite,
      // grabbed file
      grabbedFile: state.grabbedFile,
      setGrabbedFile: state.setGrabbedFile,
      setGrabbedFileLoading: state.setGrabbedFileLoading,
      setGrabbedFileError: state.setGrabbedFileError,
      resetGrabbedFile: state.resetGrabbedFile,
    }))
  );

export const useSituations = () =>
  useGlobalStore(
    useShallow((state) => ({
      situations: state.situations,
      setSituations: state.setSituations,
      setSituationsLoading: state.setSituationsLoading,
      setSituationsError: state.setSituationsError,
      resetSituations: state.resetSituations,
      // sorting
      setSituationOrder: state.setSituationOrder,
      // Initial fetch state
      initFetchedSituations: state.initFetchedSituations,
      setInitFetchedSituations: state.setInitFetchedSituations,
    }))
  );

export const useThoughts = () =>
  useGlobalStore(
    useShallow((state) => ({
      thoughts: state.thoughts,
      setThoughts: state.setThoughts,
      setThoughtsLoading: state.setThoughtsLoading,
      setThoughtsError: state.setThoughtsError,
      removeThought: state.removeThought,
      addThought: state.addThought,
      updateThought: state.updateThought,
      scoreThoughtDimension: state.scoreThoughtDimension,
      resetThoughts: state.resetThoughts,
      // sorting
      setThoughtsOrder: state.setThoughtsOrder,
      // mark initialy fetched keys
      markKeyInitialized: state.markKeyInitialized,
      unmarkKeyInitialized: state.unmarkKeyInitialized,
      isKeyInitialized: state.isKeyInitialized,
    }))
  );

export const useUnconfirmedThoughts = () =>
  useGlobalStore(
    useShallow((state) => ({
      unconfirmedThoughts: state.unconfirmedThoughts,
      setUnconfirmedThoughts: state.setUnconfirmedThoughts,
      setUnconfirmedThoughtsLoading: state.setUnconfirmedThoughtsLoading,
      setUnconfirmedThoughtsError: state.setUnconfirmedThoughtsError,
      removeUnconfirmedThought: state.removeUnconfirmedThought,
      addUnconfirmedThought: state.addUnconfirmedThought,
      updateUnconfirmedThought: state.updateUnconfirmedThought,
      resetUnconfirmedThoughts: state.resetUnconfirmedThoughts,
      // Initial fetch state
      initFetchedUnconfirmedThoughts: state.initFetchedUnconfirmedThoughts,
      setInitFetchedUnconfirmedThoughts:
        state.setInitFetchedUnconfirmedThoughts,

      // thought confirmation
      checkedUnconfirmedThoughts: state.checkedUnconfirmedThoughts,
      // setCheckedUnconfirmedThoughts: state.setCheckedUnconfirmedThoughts,
      addCheckedUnconfirmedThought: state.addCheckedUnconfirmedThought,
      removeCheckedUnconfirmedThought: state.removeCheckedUnconfirmedThought,
      resetCheckedUnconfirmedThoughts: state.resetCheckedUnconfirmedThoughts,
    }))
  );

export const useTargets = () =>
  useGlobalStore(
    useShallow((state) => ({
      targets: state.targets,
      setTargets: state.setTargets,
      setTargetsLoading: state.setTargetsLoading,
      setTargetsError: state.setTargetsError,
      resetTargets: state.resetTargets,
      // Initial fetch state
      initFetchedTargets: state.initFetchedTargets,
      setInitFetchedTargets: state.setInitFetchedTargets,
    }))
  );

export const useUserProfile = () =>
  useGlobalStore(
    useShallow((state) => ({
      userProfile: state.userProfile,
      setUserProfile: state.setUserProfile,
      setUserProfileLoading: state.setUserProfileLoading,
      setUserProfileError: state.setUserProfileError,
      resetUserProfile: state.resetUserProfile,
      // updateUserProfile: state.updateUserProfile, //! do no user, call more specific method
      updateUserProfilePreferences: state.updateUserProfilePreferences,
      // Initial state loading
      initFetchedUserProfile: state.initFetchedUserProfile,
      setInitFetchedUserProfile: state.setInitFetchedUserProfile,
    }))
  );

// A hook to access the entire global store without any specific slice
export const useWholeGlobalStore = () =>
  useGlobalStore(useShallow((state) => ({})));
